// @ts-nocheck
import { FC, useState, useEffect } from "react";
import {
  Drawer,
  Box,
  FormControl,
  useTheme,
  useMediaQuery,
  Snackbar,
  SnackbarContent,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useLanguage } from "../LanguageContext";
import { Language, openDrawerProps, CategoryData } from "../../Types/types";
import FilterHeader from "../Headers/FilterHeader";
import FilterDrawer from "./FilterDrawer";
import translationOpenDrawer from "./translationOpenDrawer";
import Button from "../Button";
import { SelectChangeEvent } from "@mui/material/Select";

const SelectedCategory: FC<{ categoryLabel: string; onDelete: () => void }> = ({
  categoryLabel,
  onDelete,
}) => (
  <Box display="flex" alignItems="center">
    <Box
      p={1}
      borderRadius="4px"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="auto"
    >
      <span style={{ fontSize: "0.875rem", marginRight: "8px" }}>
        {categoryLabel}
      </span>
      <button size="small" onClick={onDelete} className="text-white">
        <CloseIcon fontSize="small" />
      </button>
    </Box>
  </Box>
);

const OpenDrawer: FC<openDrawerProps> = ({
  open,
  onClose,
  searchTerm: initialSearchTerm,
  selectedCategories: initialSelectedCategories,
  onCategorySelect,
  categoryData = [],
  onOrderChange,
  order: initialOrder,
  onSearchClick,
  onClearClick,
  selectedSubCategories: initialSelectedSubCategories,
  onSubCategorySelect,
  subCategoryData = [],
  onSearchTermChange,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { language } = useLanguage();
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [subCategoryDrawerOpen, setSubCategoryDrawerOpen] = useState(false);
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    CategoryData[]
  >([]);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [selectedCategories, setSelectedCategories] = useState(
    initialSelectedCategories
  );
  const [selectedSubCategories, setSelectedSubCategories] = useState(
    initialSelectedSubCategories
  );
  const [order, setOrder] = useState<string | undefined>(initialOrder || "ASC");
  const t = translationOpenDrawer[language as Language];
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    setSearchTerm(initialSearchTerm);
    setSelectedCategories(initialSelectedCategories);
    setSelectedSubCategories(initialSelectedSubCategories);
    setOrder(initialOrder || "ASC");
  }, [
    initialSearchTerm,
    initialSelectedCategories,
    initialSelectedSubCategories,
    initialOrder,
  ]);

  const toggleCategoryDrawer = (open: boolean) => () => {
    setCategoryDrawerOpen(open);
  };

  const toggleSubCategoryDrawer = (open: boolean) => () => {
    setSubCategoryDrawerOpen(open);
  };

  const handleOrderToggle = () => {
    const newOrder = order === "ASC" ? "DESC" : "ASC";
    setOrder(newOrder);

    const syntheticEvent = {
      target: { value: newOrder },
    } as SelectChangeEvent<string>;

    onOrderChange(syntheticEvent);
  };

  const handleSearchClick = () => {
    onSearchClick();
  };

  const handleSubCategoryButtonClick = () => {
    if (selectedCategories.length === 0) {
      setShowSnackbar(true);
      return;
    }

    const selectedCategoryName = categoryData.find(
      (category) =>
        category.id.toString() ===
        selectedCategories[selectedCategories.length - 1]
    )?.name;

    if (selectedCategoryName) {
      const normalizedCategoryName = selectedCategoryName
        .normalize("NFC")
        .toLowerCase()
        .trim();
      const matchingSubCategories = subCategoryData
        .filter(
          (subCategory) =>
            subCategory.category.normalize("NFC").toLowerCase().trim() ===
            normalizedCategoryName
        )
        .map((subCategory) => ({
          id: subCategory.id,
          name: subCategory.name,
          label: subCategory.name,
          order: 0,
          category: subCategory.category,
        }));
      setFilteredSubCategories(matchingSubCategories);
      setSubCategoryDrawerOpen(true);
    }
  };

  const selectedCategoryLabels = selectedCategories.map((categoryId) => {
    const category = categoryData.find(
      (cat) => cat.id.toString() === categoryId
    );
    return category ? category.name : "";
  });

  const selectedSubCategoryLabels = selectedSubCategories.map(
    (subCategoryId) => {
      const subCategory = subCategoryData.find(
        (subCat) => subCat.id.toString() === subCategoryId
      );
      return subCategory ? subCategory.name : "";
    }
  );

  const handleCategoryDelete = (categoryId: string) => {
    const updatedCategories = selectedCategories.filter(
      (id) => id !== categoryId
    );
    setSelectedCategories(updatedCategories);
    onCategorySelect(updatedCategories);

    if (updatedCategories.length === 0) {
      setSelectedSubCategories([]);
      onSubCategorySelect([]);
    }
  };

  const handleSubCategoryDelete = (subCategoryId: string) => {
    const updatedSubCategories = selectedSubCategories.filter(
      (id) => id !== subCategoryId
    );
    setSelectedSubCategories(updatedSubCategories);
    onSubCategorySelect(updatedSubCategories);
  };

  const handleSearchTermChange = (e: any) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    onSearchTermChange(newSearchTerm);
  };

  return (
    <div>
      <Drawer
        className="font-sans"
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: isSmallScreen ? "100%" : 380,
            minWidth: 320,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            overflow: "hidden",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(88, 110, 223, .8)",
          },
        }}
      >
        <div
          className="bg-white dark:bg-darkSecondaryBackground flex flex-col h-dvh"
        >
          <FilterHeader onClose={onClose} />
          <div className="p-4 flex-1 flex flex-col overflow-y-auto">
            <div className="relative mb-4">
              <input
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg bg-white dark:bg-darkBackground dark:border-gray-600 dark:text-white outline-none focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm"
                placeholder={t.search}
                value={searchTerm}
                onChange={handleSearchTermChange}
              />
              <SearchIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>

            <div className="flex-1">
              <p className="px-2 text-xs uppercase tracking-wider text-gray-500 dark:text-white mb-3">
                Filters
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={toggleCategoryDrawer(true)}
                  className="flex items-center justify-between px-4 py-3 rounded-lg text-gray-800 dark:text-white bg-white shadow-sm dark:bg-darkBackground/60 dark:border dark:border-purple/30 hover:shadow-md dark:hover:bg-darkBackground/70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
                >
                  <span className="font-medium">{t.categories}</span>
                </button>
                {selectedCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-2">
                    {selectedCategoryLabels.map((label, index) =>
                      label ? (
                        <div key={selectedCategories[index]} className="inline-flex items-center gap-2 bg-purple/10 dark:bg-purple/20 border border-purple/30 text-purple dark:text-purple-300 px-3 py-1 rounded-lg text-sm">
                          <span>{label}</span>
                          <button
                            onClick={() => handleCategoryDelete(selectedCategories[index])}
                            className="hover:bg-purple/20 dark:hover:bg-purple/30 rounded p-0.5 transition-colors"
                          >
                            <CloseIcon fontSize="small" />
                          </button>
                        </div>
                      ) : null
                    )}
                  </div>
                )}
                <button
                  onClick={handleSubCategoryButtonClick}
                  className="flex items-center justify-between px-4 py-3 rounded-lg text-gray-800 dark:text-white bg-white shadow-sm dark:bg-darkBackground/60 dark:border dark:border-purple/30 hover:shadow-md dark:hover:bg-darkBackground/70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
                >
                  <span className="font-medium">{t.subCategories}</span>
                </button>
                {selectedSubCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-2">
                    {selectedSubCategoryLabels.map((label, index) =>
                      label ? (
                        <div key={selectedSubCategories[index]} className="inline-flex items-center gap-2 bg-purple/10 dark:bg-purple/20 border border-purple/30 text-purple dark:text-purple-300 px-3 py-1 rounded-lg text-sm">
                          <span>{label}</span>
                          <button
                            onClick={() => handleSubCategoryDelete(selectedSubCategories[index])}
                            className="hover:bg-purple/20 dark:hover:bg-purple/30 rounded p-0.5 transition-colors"
                          >
                            <CloseIcon fontSize="small" />
                          </button>
                        </div>
                      ) : null
                    )}
                  </div>
                )}
                <button
                  onClick={handleOrderToggle}
                  className="flex items-center justify-between px-4 py-3 rounded-lg text-gray-800 dark:text-white bg-white shadow-sm dark:bg-darkBackground/60 dark:border dark:border-purple/30 hover:shadow-md dark:hover:bg-darkBackground/70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
                >
                  <span className="font-medium">
                    {order === "ASC"
                      ? `${t.order} ${t["ascending A-Z"]}`
                      : `${t.order} ${t["descending Z-A"]}`}
                  </span>
                </button>
              </div>
            </div>

            <div
              className="flex flex-col gap-2 mt-auto pt-4"
              style={{ paddingBottom: "env(safe-area-inset-bottom, 20px)" }}
            >
              <Button
                buttonText={t.search}
                onClick={handleSearchClick}
                className="bg-purple hover:bg-darkPurple text-white font-semibold px-4 py-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50 transition-all"
              />
              <Button
                buttonText={t.clear}
                onClick={onClearClick}
                className="bg-lightPurple text-black dark:text-white dark:bg-darkBackground/60 ring-1 ring-purple/40 dark:ring-purple/40 hover:bg-lightPurple/90 dark:hover:bg-darkBackground/70 font-semibold px-4 py-2 rounded-lg transition-all"
              />
            </div>
          </div>
        </div>
      </Drawer>

      <FilterDrawer
        open={categoryDrawerOpen}
        onClose={toggleCategoryDrawer(false)}
        categories={categoryData.map((category) => ({
          id: category.id,
          name: category.name,
          label: category.label,
          order: category.order,
          category: category.category,
        }))}
        subCategories={[]}
        selectedCategories={selectedCategories}
        selectedSubCategories={selectedSubCategories}
        onCategorySelect={(updatedCategories) => {
          setSelectedCategories(updatedCategories);
          onCategorySelect(updatedCategories);

          if (updatedCategories.length === 0) {
            setSelectedSubCategories([]);
            onSubCategorySelect([]);
          }
        }}
        onSubCategorySelect={(updatedSubCategories) => {
          setSelectedSubCategories(updatedSubCategories);
          onSubCategorySelect(updatedSubCategories);
        }}
        isSubCategoryDrawer={false}
      />

      <FilterDrawer
        open={subCategoryDrawerOpen}
        onClose={toggleSubCategoryDrawer(false)}
        categories={[]}
        subCategories={filteredSubCategories}
        selectedCategories={selectedCategories}
        selectedSubCategories={selectedSubCategories}
        onCategorySelect={(updatedCategories) => {
          setSelectedCategories(updatedCategories);
          onCategorySelect(updatedCategories);
        }}
        onSubCategorySelect={(updatedSubCategories) => {
          setSelectedSubCategories(updatedSubCategories);
          onSubCategorySelect(updatedSubCategories);
        }}
        isSubCategoryDrawer={true}
      />
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <SnackbarContent
          message={t.selectCategory}
          sx={{ backgroundColor: "orange", color: "white" }}
        />
      </Snackbar>
    </div>
  );
};

export default OpenDrawer;
